import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, CARD_DESCRIPTOR, siteImages } from '../data/mock';
import { FileText, Bolt } from 'lucide-react';

const TermsPage = () => {
  useEffect(() => {
    document.title = 'Terms & Conditions | Axon Muscle';
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-56 lg:h-64 overflow-hidden">
          <img 
            src={siteImages.privacyBanner}
            alt="Terms and Conditions"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
          
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-axon-600/20 border border-axon-500/30 rounded-full text-axon-400 text-sm font-semibold mb-4">
                <Bolt className="w-4 h-4" />
                LEGAL
              </div>
              <h1 className="font-display text-4xl lg:text-5xl text-white uppercase tracking-tight">
                Terms & Conditions
              </h1>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-neutral-900 rounded-3xl border border-neutral-800 p-8 lg:p-12">
            {/* Company Info Card */}
            <div className="mb-10 p-6 bg-axon-600/10 border border-axon-500/30 rounded-2xl">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="w-6 h-6 text-axon-500" />
                <span className="text-axon-400 font-semibold">These Terms & Conditions are provided by:</span>
              </div>
              <p className="text-white font-semibold text-lg">{CONTACT_INFO.corpName}</p>
              <p className="text-neutral-400">{CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</p>
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-neutral-300 mb-8">
                Last Updated: January 2025
              </p>

              <div className="space-y-8">
                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">1. Agreement to Terms</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    By accessing or using our website, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">2. Products and Services</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    All products sold on this website are dietary supplements. These products are not intended to diagnose, treat, cure, or prevent any disease. Always consult with a healthcare professional before starting any supplement regimen.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">3. Ordering and Payment</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    By placing an order, you agree to provide accurate and complete information. All prices are in USD. Payment is processed securely and charges will appear on your statement as "<span className="text-axon-400 font-semibold">{CARD_DESCRIPTOR}</span>".
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">4. Shipping</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    We ship to addresses within the United States. Shipping times may vary based on location. Free shipping is available on orders over $99. We are not responsible for delays caused by shipping carriers.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">5. Returns and Refunds</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    We offer a 30-day return policy on unopened products. Please see our Returns page for complete details. Refunds will be processed to the original payment method within 5-7 business days after we receive the returned item.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">6. Intellectual Property</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    All content on this website, including text, graphics, logos, and images, is the property of {CONTACT_INFO.corpName} and is protected by copyright laws. You may not reproduce or distribute any content without our written permission.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">7. Limitation of Liability</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    {CONTACT_INFO.corpName} shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our products or services. Our liability is limited to the amount paid for the product.
                  </p>
                </section>

                <section>
                  <h2 className="font-display text-xl text-white uppercase tracking-wide mb-4">8. Contact Information</h2>
                  <p className="text-neutral-400 leading-relaxed">
                    For questions about these Terms & Conditions, please contact us:
                  </p>
                  <div className="mt-4 p-4 bg-neutral-800 rounded-xl">
                    <p className="text-white font-semibold">{CONTACT_INFO.corpName}</p>
                    <p className="text-neutral-400">Email: {CONTACT_INFO.email}</p>
                    <p className="text-neutral-400">Phone: {CONTACT_INFO.phone}</p>
                    <p className="text-neutral-400">{CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TermsPage;
